<template>
  <div id="main" ref="main">
    <svg :width="width" :height="height">
      <g :transform="'translate(' + margin.left + ',' + margin.top + ')'">
        <g id="dots" ref="dots">
          <!-- SCATTERPLOT -->
          <template v-if="selectedYAttribute != null">
            <circle v-for="county in countyData" :key="county.BU_CODE"
              :cx="xScale(+county[selectedXAttribute.id])"
              :cy="yScale(+county[selectedYAttribute.id])"
              :r="dotRadius"
              :fill="getColor(county)"
              :class="{ selected: county.BU_CODE == idSelectedCounty }"
              :ref="county.BU_CODE"
              @mouseover="hoverIn(county)"
              @mouseleave="hoverOut">
            </circle>
          </template>
          <!-- HISTOGRAM -->
          <template v-else>
            <template v-for="bin in bins">
              <circle v-for="(county,index) in bin" :key="county.BU_CODE"
                :cx="0.5 * (xScale(bin.x0) + xScale(bin.x1))"
                :cy="yScale(index)"
                :r="dotRadius"
                :fill="getColor(county)"
                :class="{ selected: county.BU_CODE == idSelectedCounty }"
                :ref="county.BU_CODE"
                @mouseover="hoverIn(county)"
                @mouseleave="hoverOut">
              </circle>
            </template>
          </template>
        </g>
        <g ref="xAxis"
          :transform="'translate(0,' + (height - margin.top - margin.bottom) +')'"></g>
        <g ref="yAxis"></g>
      </g>
    </svg>
  </div>
</template>

<style scoped>
  circle.selected {
    stroke-width: 3px;
    stroke: #ff984a;
  }
</style>

<style>
.label {
  font-size: 12pt;
  font-family: Roboto, sans-serif;
  fill: #333333;
}
text {
  user-select: none;
  pointer-events: none;
}

</style>

<script>
  import * as D3 from 'd3';

  export default {
    data: () => ({
      width: 0,
      height: 0,
      xScale: D3.scaleLinear(),
      yScale: D3.scaleLinear(),
      xAxis: null,
      yAxis: null,
      margin: {left: 50, right: 20, top:20, bottom: 40},
      dotColor: '#007bc777',
      dotRadius: 10,
      nrBins: 10, //in histogram
      bins: []
    }),
    mounted() {
      this.width = this.$refs.main.clientWidth;
      this.height = this.$refs.main.clientHeight;

      this.xScale.range([0,this.width - this.margin.left - this.margin.right]);
      this.yScale.range([this.height - this.margin.top - this.margin.bottom, 0]);

      this.xAxis = D3.axisBottom(this.xScale);
      this.yAxis = D3.axisLeft(this.yScale);
    },
    computed: {
      labels() {
        return this.$store.getters.labels;
      },
      countyData() {
        return this.$store.getters.data;
      },
      selectedXAttribute() {
        return this.$store.getters.selectedXAttribute;
      },
      selectedYAttribute() {
        return this.$store.getters.selectedYAttribute;
      },
      currentLanguage() {
        return this.$store.getters.currentLanguage;
      },
      attributes() {
        return this.$store.getters.attributes;
      },
      xAxisLabel() {
        return this.selectedXAttribute.label[this.currentLanguage];
      },
      yAxisLabel() {
        return this.selectedYAttribute != null ? this.selectedYAttribute.label[this.currentLanguage]
          : this.labels["count"][this.currentLanguage];
      },
      maxValueColorAttribute() {
        return D3.max(this.countyData, (d) => +d[this.selectedXAttribute.id]);
      },
      idSelectedCounty() {
        return this.$store.getters.idSelectedCounty;
      }
    },
    watch: {
      countyData() {
        this.updateDomainsAndBins();
        this.updateAxes();
      },
      selectedXAttribute() {
        this.updateDomainsAndBins();
        this.updateAxes();
      },
      selectedYAttribute() {
        this.updateDomainsAndBins();
        this.updateAxes();
      },
      currentLanguage() {
        this.updateAxes();
      },
      idSelectedCounty() {
        //bring svg element of selected item to front
        if (this.idSelectedCounty != null) {
          this.$refs.dots.appendChild(this.$refs[this.idSelectedCounty][0]);
        }
      }
    },
    methods: {
      updateDomainsAndBins() {
        this.xScale.domain([0, D3.max(this.countyData, (d) => +d[this.selectedXAttribute.id])]);
        let histogram = D3.histogram()
          .value(d => d[this.selectedXAttribute.id])
          .domain(this.xScale.domain())
          .thresholds(this.xScale.ticks(this.nrBins));
        let bins = histogram(this.countyData);
        //sort data within each bin
        bins.forEach(b => {
          b.sort((c1, c2) => {
            return c2[this.selectedXAttribute.id] - c1[this.selectedXAttribute.id];
          });
        })
        this.bins = bins;
        if (this.selectedYAttribute != null) {
          //scatterplot
          this.yScale.domain([0, D3.max(this.countyData, (d) => +d[this.selectedYAttribute.id])]);
        } else {
          //histogram
          this.yScale.domain([0, D3.max(this.bins, d => d.length)])
        }
      },
      updateAxes() {
        D3.select(this.$refs.xAxis).selectAll("*").remove();
        D3.select(this.$refs.xAxis)
          .call(this.xAxis)
          .selectAll("text")
          .style("text-anchor", "end")
          .attr("dx", "-.8em")
          .attr("dy", "-.55em")
          .attr("transform", "rotate(-90)") //rotate axis numbers
        D3.select(this.$refs.xAxis)
          .append("text")
            .attr("class", "label")
            .attr("x", this.width-this.margin.left-this.margin.right)
            .attr("y", -6)
            .style("text-anchor", "end")
            .text(this.xAxisLabel);

        D3.select(this.$refs.yAxis).selectAll("*").remove();
        D3.select(this.$refs.yAxis)
          .call(this.yAxis)
          .append("text")
            .attr("class", "label")
            .attr("x", -this.margin.left + 5)
            //.attr("transform", "rotate(-90)")
            .attr("y", -20)
            .attr("dy", ".71em") //font height
            .style("text-anchor", "start")
            .text(this.yAxisLabel);
      },
      getColor(item) {
        let value = +item[this.selectedXAttribute.id];
        if (value < 0) return '#aaaaaa';
        return D3.interpolateViridis(value/this.maxValueColorAttribute);
      },
      hoverIn(item) {
        this.$store.commit("SET_ID_SELECTED_COUNTY", item.BU_CODE);
      },
      hoverOut() {
        this.$store.commit("SET_ID_SELECTED_COUNTY", null);
      }
    }

  }

</script>
